import { useState, useMemo, useEffect } from "react";
import { Select, Stack, Text, Box, ScrollArea, Group } from "@mantine/core";
import * as Diff from "diff";

export default function FlowVersionDiff({ flowTemplates, flow, selectedVersion }) {
  const [versionA, setVersionA] = useState(null);
  const [versionB, setVersionB] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Auto-select: active version as "old", current version as "new"
  useEffect(() => {
    if (!flowTemplates?.length || !flow) return;

    // Find the active version (first active env's version)
    const activeVersionId = flow.activeVersions
      ? Object.values(flow.activeVersions)[0]
      : null;

    if (activeVersionId && !versionA) {
      setVersionA(activeVersionId);
    }
    if (selectedVersion?.id && !versionB) {
      setVersionB(selectedVersion.id);
    }
  }, [flowTemplates, flow, selectedVersion, versionA, versionB]);

  // Build version options with incrementing version numbers (oldest = v1)
  const { versionOptions, versionMap } = useMemo(() => {
    const sorted = [...flowTemplates].sort(
      (a, b) => new Date(a.created) - new Date(b.created)
    );
    const idToNum = new Map(sorted.map((v, i) => [v.id, i + 1]));
    const map = new Map();
    const options = flowTemplates.map((v) => {
      const num = idToNum.get(v.id);
      const d = new Date(v.created);
      const ts = `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
      map.set(v.id, { num, ts, templateCount: v.templates.length });
      return {
        value: v.id,
        label: `v${num}`,
        num,
        ts,
      };
    });
    return { versionOptions: options, versionMap: map };
  }, [flowTemplates]);

  const renderVersionOption = ({ option }) => {
    const info = versionMap.get(option.value);
    if (!info) return option.label;
    return (
      <Box>
        <Text size="sm" fw={500}>v{info.num} ({info.templateCount} templates)</Text>
        <Text size="xs" c="dimmed">{info.ts}</Text>
      </Box>
    );
  };

  // Get templates available in either version
  const templateOptions = useMemo(() => {
    const vA = flowTemplates.find((v) => v.id === versionA);
    const vB = flowTemplates.find((v) => v.id === versionB);
    const names = new Set();
    if (vA) vA.templates.forEach((t) => names.add(t.name));
    if (vB) vB.templates.forEach((t) => names.add(t.name));
    return Array.from(names).map((name) => ({ value: name, label: name }));
  }, [versionA, versionB, flowTemplates]);

  // Auto-select template if only one exists
  useEffect(() => {
    if (templateOptions.length === 1 && !selectedTemplate) {
      setSelectedTemplate(templateOptions[0].value);
    }
  }, [templateOptions, selectedTemplate]);

  // Compute diff
  const diffResult = useMemo(() => {
    if (!versionA || !versionB || !selectedTemplate) return null;

    const vA = flowTemplates.find((v) => v.id === versionA);
    const vB = flowTemplates.find((v) => v.id === versionB);

    const templateA = vA?.templates.find((t) => t.name === selectedTemplate);
    const templateB = vB?.templates.find((t) => t.name === selectedTemplate);

    const contentA = templateA?.template || "";
    const contentB = templateB?.template || "";

    if (!templateA && !templateB) return null;

    if (!templateA) {
      return { type: "added", content: contentB };
    }
    if (!templateB) {
      return { type: "removed", content: contentA };
    }

    const changes = Diff.diffLines(contentA, contentB);
    return { type: "diff", changes };
  }, [versionA, versionB, selectedTemplate, flowTemplates]);

  const renderDiff = () => {
    if (!diffResult) {
      return (
        <Text c="dimmed" size="sm">
          Select two versions and a template to compare
        </Text>
      );
    }

    if (diffResult.type === "added") {
      return (
        <Box>
          <Text size="sm" c="green" mb="xs">
            Template only exists in Version B (added)
          </Text>
          <Box
            p="xs"
            style={{
              backgroundColor: "var(--mantine-color-green-light)",
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              fontSize: "12px",
            }}
          >
            {diffResult.content}
          </Box>
        </Box>
      );
    }

    if (diffResult.type === "removed") {
      return (
        <Box>
          <Text size="sm" c="red" mb="xs">
            Template only exists in Version A (removed)
          </Text>
          <Box
            p="xs"
            style={{
              backgroundColor: "var(--mantine-color-red-light)",
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              fontSize: "12px",
            }}
          >
            {diffResult.content}
          </Box>
        </Box>
      );
    }

    return (
      <Box
        style={{
          fontFamily: "monospace",
          fontSize: "12px",
          lineHeight: "1.5",
        }}
      >
        {diffResult.changes.map((part, index) => {
          const style = {
            backgroundColor: part.added
              ? "var(--mantine-color-green-light)"
              : part.removed
                ? "var(--mantine-color-red-light)"
                : "transparent",
            whiteSpace: "pre-wrap",
            display: "block",
          };
          const prefix = part.added ? "+ " : part.removed ? "- " : "  ";
          return (
            <span key={index} style={style}>
              {part.value
                .split("\n")
                .filter((line, i, arr) => i < arr.length - 1 || line !== "")
                .map((line, i) => (
                  <div key={i}>
                    {prefix}
                    {line}
                  </div>
                ))}
            </span>
          );
        })}
      </Box>
    );
  };

  return (
    <Stack>
      <Group grow>
        <Select
          label="Version A (old)"
          placeholder="Select version"
          data={versionOptions}
          value={versionA}
          onChange={setVersionA}
          renderOption={renderVersionOption}
          searchable
        />
        <Select
          label="Version B (new)"
          placeholder="Select version"
          data={versionOptions}
          value={versionB}
          onChange={setVersionB}
          renderOption={renderVersionOption}
          searchable
        />
      </Group>

      <Select
        label="Template"
        placeholder="Select template to compare"
        data={templateOptions}
        value={selectedTemplate}
        onChange={setSelectedTemplate}
        disabled={!versionA || !versionB}
      />

      <ScrollArea h={500} type="auto">
        <Box p="xs" style={{ border: "1px solid var(--mantine-color-gray-3)" }}>
          {renderDiff()}
        </Box>
      </ScrollArea>
    </Stack>
  );
}
